import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  d203Api, FD130, FD131, FD132, FD133, FD135, FD136,
} from '../../../services/d203Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useD203Mode';

export function useD203Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FD130 | null>(null);
  const [formData, setFormData] = useState<Partial<FD130>>({});
  const [details, setDetails] = useState<Partial<FD131>[]>([]);
  const [productDesc, setProductDesc] = useState<Partial<FD132>>({});
  const [compositions, setCompositions] = useState<Partial<FD133>[]>([]);
  const [accessories, setAccessories] = useState<Partial<FD135>[]>([]);
  const [inspections, setInspections] = useState<Partial<FD136>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FD130;
    dets: Partial<FD131>[];
    pd: Partial<FD132>;
  } | null> => {
    setLoading(true);
    try {
      const data = await d203Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setProductDesc(data.product_desc || {});
      setCompositions(data.compositions || []);
      setAccessories(data.accessories || []);
      setInspections(data.inspections || []);
      setSelectedRow(0);
      return { data, dets, pd: data.product_desc || {} };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FD130, value: FD130[keyof FD130]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FD130): FD130[keyof FD130] => {
    return (formData as Record<string, FD130[keyof FD130]>)[key] ?? '';
  }, [formData]);

  /** Set a product desc field */
  const setPDField = useCallback((key: keyof FD132, value: FD132[keyof FD132]) => {
    setProductDesc(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a product desc field */
  const getPDField = useCallback((key: keyof FD132): FD132[keyof FD132] => {
    return (productDesc as Record<string, FD132[keyof FD132]>)[key] ?? '';
  }, [productDesc]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FD131): FD131[keyof FD131] => {
    const sel = details[selectedRow] as Partial<FD131> | undefined;
    return sel ? (sel as Record<string, FD131[keyof FD131]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FD131, value: FD131[keyof FD131]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FD130) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FD130,
        details: details as FD131[],
        product_desc: productDesc as FD132,
        compositions: compositions as FD133[],
        accessories: accessories as FD135[],
        inspections: inspections as FD136[],
      };
      if (mode === 'add') {
        const result = await d203Api.create(req);
        message.success(`新增成功: ${result.fd130_01}`);
        onSuccess(result);
      } else {
        const result = await d203Api.update(formData.fd130_01!, req);
        message.success('修改成功');
        applyResult(result);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, productDesc, compositions, accessories, inspections, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await d203Api.delete(record.fd130_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row (FD131) */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fd131_02 || 0), 0);
    setDetails(prev => [...prev, { fd131_02: maxSeq + 1, fd131_01: formData.fd130_01 }]);
  }, [details, formData.fd130_01]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fd131_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Add a composition row (FD133) */
  const addCompositionRow = useCallback(() => {
    const maxSeq = compositions.reduce((max, c) => Math.max(max, c.fd133_02 || 0), 0);
    setCompositions(prev => [...prev, { fd133_02: maxSeq + 1, fd133_01: formData.fd130_01 }]);
  }, [compositions, formData.fd130_01]);

  /** Delete a composition row */
  const deleteCompositionRow = useCallback((idx: number) => {
    setCompositions(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((c, i) => ({ ...c, fd133_02: i + 1 }));
    });
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
      setProductDesc(record.product_desc || {});
      setCompositions(record.compositions || []);
      setAccessories(record.accessories || []);
      setInspections(record.inspections || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fd130_45: 'NTD',
      fd130_46: 1,
      fd130_48: 1,
      fd130_31: 1,
      fd130_40: 1,
      fd130_26: 'N',
      fd130_80: 'N',
      fd130_37: 1,
      fd130_52: true,
      fd130_79: '1',
      fd130_33: user?.userId || '',
    });
    setDetails([{ fd131_02: 1 }]);
    setProductDesc({});
    setCompositions([]);
    setAccessories([]);
    setInspections([]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  const importSCInspections = useCallback(async (scNo: string, lineSeq: number) => {
    if (!scNo.trim() || lineSeq <= 0) return;
    try {
      const rows = await d203Api.previewSCInspections(scNo.trim(), lineSeq);
      setInspections(rows);
      setFormData(prev => ({ ...prev, fd130_44: rows.length }));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '檢驗規範帶入失敗');
    }
  }, []);

  /** Apply result after save */
  const applyResult = useCallback((result: FD130) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
    setProductDesc(result.product_desc || {});
    setCompositions(result.compositions || []);
    setAccessories(result.accessories || []);
    setInspections(result.inspections || []);
  }, []);

  return {
    record, formData, details, productDesc, compositions, accessories, inspections,
    selectedRow, loading,
    setSelectedRow, setDetails, setCompositions,
    loadRecord, setField, getField,
    setPDField, getPDField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    addCompositionRow, deleteCompositionRow,
    revertToRecord, resetForAdd, applyResult, importSCInspections,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
